/**
 * Original vector artwork (no third-party assets). Rendered inline so it ships in the APK, scales crisply on any
 * density, and animates with CSS (all motion stops under prefers-reduced-motion).
 *
 * NightRoadScene is Runner Boy's signature visual: an Indian city at night, the car parked, the runner bringing the
 * order home. Shared by the consumer app, the store app and the Control presentation.
 */
import { useId, type CSSProperties } from 'react';

const AMBER = '#FFAA1F';

/** Deterministic pseudo-random for window lights (stable between renders). */
function rnd(seed: number) {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

export function NightRoadScene({ className, animated = true }: { className?: string; animated?: boolean }) {
  const id = useId().replace(/:/g, '');
  const tw = animated ? 'rb-twinkle' : '';
  // Far skyline: towers, a temple shikhara, a dome and a clock tower.
  const towers = [
    [0, 92, 34], [30, 74, 26], [52, 100, 30], [80, 62, 22], [98, 86, 30], [124, 70, 24],
    [214, 78, 26], [236, 58, 20], [252, 90, 30], [278, 66, 24], [298, 96, 28], [322, 72, 38],
  ];
  return (
    <svg viewBox="0 0 360 220" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id={`sky${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#060A17" />
          <stop offset="0.55" stopColor="#121C3D" />
          <stop offset="1" stopColor="#1F2B57" />
        </linearGradient>
        <radialGradient id={`moon${id}`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#FFE9B8" stopOpacity="0.55" />
          <stop offset="1" stopColor="#FFE9B8" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`win${id}`} cx="0.5" cy="0.5" r="0.6">
          <stop offset="0" stopColor="#FFE3A6" />
          <stop offset="1" stopColor={AMBER} />
        </radialGradient>
        <radialGradient id={`glow${id}`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor={AMBER} stopOpacity="0.55" />
          <stop offset="1" stopColor={AMBER} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`beam${id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#FFF3D6" stopOpacity="0.85" />
          <stop offset="1" stopColor="#FFF3D6" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`road${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#151B2E" />
          <stop offset="1" stopColor="#070A14" />
        </linearGradient>
      </defs>

      <rect width="360" height="220" fill={`url(#sky${id})`} />
      {/* stars */}
      {Array.from({ length: 22 }, (_, i) => (
        <circle key={i} cx={rnd(i) * 360} cy={rnd(i + 40) * 70} r={rnd(i + 80) * 0.9 + 0.4} fill="#FFFFFF" opacity={0.35 + rnd(i + 3) * 0.5} className={i % 4 === 0 ? tw : ''} style={{ animationDelay: `${(i % 7) * 0.4}s` }} />
      ))}
      {/* moon */}
      <circle cx="300" cy="40" r="34" fill={`url(#moon${id})`} />
      <circle cx="300" cy="40" r="13" fill="#FFF1CF" />
      <circle cx="306" cy="35" r="11.5" fill="#121C3D" />

      {/* far skyline */}
      <g fill="#18234A">
        {towers.map(([x, h, w], i) => (
          <rect key={i} x={x} y={150 - (h as number)} width={w} height={h} />
        ))}
        {/* temple shikhara */}
        <path d="M150 150 V112 L158 104 L166 84 L172 66 L178 84 L186 104 L194 112 V150 Z" />
        <rect x="170" y="58" width="4" height="10" />
        {/* dome + minaret */}
        <path d="M196 150 V118 A14 14 0 0 1 224 118 V150 Z" />
        <rect x="226" y="96" width="5" height="54" />
        <circle cx="228.5" cy="94" r="3.5" />
        {/* clock tower */}
        <rect x="132" y="96" width="14" height="54" />
        <path d="M132 96 L139 84 L146 96 Z" />
      </g>
      <circle cx="139" cy="104" r="3.2" fill="#2B3A73" />
      {/* lit windows */}
      {towers.flatMap(([x, h, w], ti) =>
        Array.from({ length: 6 }, (_, k) => {
          const seed = ti * 13 + k;
          if (rnd(seed) < 0.45) return null;
          const wx = (x as number) + 4 + Math.floor(rnd(seed + 1) * Math.max(1, (w as number) - 10));
          const wy = 150 - (h as number) + 6 + Math.floor(rnd(seed + 2) * Math.max(1, (h as number) - 16));
          return <rect key={`${ti}-${k}`} x={wx} y={wy} width="3" height="4" rx="0.6" fill={AMBER} opacity={0.5 + rnd(seed + 5) * 0.5} className={seed % 5 === 0 ? tw : ''} style={{ animationDelay: `${(seed % 9) * 0.35}s` }} />;
        }),
      )}

      {/* near ground + home ("your place") */}
      <rect x="0" y="148" width="360" height="12" fill="#0D1430" />
      <g>
        <circle cx="282" cy="132" r="40" fill={`url(#glow${id})`} />
        <path d="M244 160 V118 L282 92 L320 118 V160 Z" fill="#0F1734" />
        <path d="M238 120 L282 88 L326 120" fill="none" stroke="#2C3F91" strokeWidth="5" strokeLinejoin="round" strokeLinecap="round" />
        <rect x="256" y="124" width="22" height="18" rx="3" fill={`url(#win${id})`} />
        <path d="M267 124 V142 M256 133 H278" stroke="#B26B00" strokeWidth="1.2" opacity="0.6" />
        <rect x="288" y="128" width="17" height="32" rx="2" fill="#1A2550" />
        <circle cx="301" cy="145" r="1.6" fill={AMBER} />
      </g>

      {/* road */}
      <rect x="0" y="160" width="360" height="60" fill={`url(#road${id})`} />
      <rect x="0" y="160" width="360" height="2" fill="#2A3560" />
      <g className={animated ? 'rb-lane' : ''} style={{ transformBox: 'fill-box' } as CSSProperties}>
        {Array.from({ length: 7 }, (_, i) => (
          <rect key={i} x={i * 80 + 6} y="190" width="44" height="4" rx="2" fill={AMBER} opacity="0.55" />
        ))}
      </g>

      {/* parked car + stop sign: the car stays home tonight */}
      <g transform="translate(22 150)">
        <ellipse cx="46" cy="41" rx="46" ry="4" fill="#000" opacity="0.35" />
        <path d="M6 30 Q10 15 26 13 H58 Q70 13 78 26 L86 30 Q92 32 92 37 V41 H0 V36 Q0 31 6 30 Z" fill="#323C5C" />
        <path d="M23 17 H52 L61 27 H15 Z" fill="#46527A" />
        <rect x="84" y="31" width="6" height="3" rx="1" fill="#59618A" />
        <circle cx="20" cy="42" r="8" fill="#0A0E1C" stroke="#59618A" strokeWidth="2" />
        <circle cx="72" cy="42" r="8" fill="#0A0E1C" stroke="#59618A" strokeWidth="2" />
      </g>
      <g transform="translate(122 118)">
        <rect x="9" y="22" width="3" height="40" fill="#8E96B3" />
        <polygon points="4,0 17,0 22,8 22,18 17,26 4,26 -1,18 -1,8" fill="#E5484D" />
        <rect x="3" y="11" width="15" height="4" rx="1" fill="#FFFFFF" />
      </g>

      {/* runner on a scooter, heading home */}
      <g transform="translate(166 150)">
        <g className={animated ? 'rb-float' : ''} style={{ transformBox: 'fill-box' } as CSSProperties}>
          <path d="M58 22 L150 6 L150 46 Z" fill={`url(#beam${id})`} className={animated ? 'rb-beam' : ''} opacity="0.7" />
          <path d="M-30 30 H-4 M-38 21 H-10 M-24 39 H-2" stroke={AMBER} strokeWidth="2.6" strokeLinecap="round" opacity="0.55" />
          <circle cx="2" cy="40" r="10" fill="#0A0E1C" stroke="#C9CEDC" strokeWidth="2.2" />
          <circle cx="54" cy="40" r="10" fill="#0A0E1C" stroke="#C9CEDC" strokeWidth="2.2" />
          <path d="M2 40 L18 18 H40 L54 40" fill="none" stroke="#F7F4EE" strokeWidth="6.5" strokeLinejoin="round" strokeLinecap="round" />
          <path d="M40 18 L48 6 H56" fill="none" stroke="#F7F4EE" strokeWidth="4" strokeLinecap="round" />
          <circle cx="57" cy="20" r="3.6" fill="#FFF3D6" />
          {/* delivery box */}
          <rect x="4" y="-4" width="24" height="20" rx="4" fill={AMBER} />
          <rect x="4" y="2" width="24" height="3" fill="#E28C00" />
          {/* rider */}
          <path d="M30 18 L36 2 L46 -2" fill="none" stroke="#1D2D70" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="40" cy="-12" r="8.5" fill="#F7F4EE" />
          <path d="M33 -13 A8.5 8.5 0 0 1 48 -15" fill="none" stroke={AMBER} strokeWidth="3" strokeLinecap="round" />
        </g>
      </g>
    </svg>
  );
}

/** Storefront illustration for store cards, coloured deterministically per store. */
export function StorefrontArt({ name, className }: { name: string; className?: string }) {
  const id = useId().replace(/:/g, '');
  const palettes = [
    ['#1D2D70', '#FFAA1F'],
    ['#0E1324', '#FFAA1F'],
    ['#2C3F91', '#FFDF9E'],
    ['#172459', '#FF8A4C'],
  ];
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const [base, stripe] = palettes[h % palettes.length]!;
  return (
    <svg viewBox="0 0 300 120" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id={`sf${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={base} />
          <stop offset="1" stopColor="#070B16" />
        </linearGradient>
        <radialGradient id={`sg${id}`} cx="0.5" cy="0.6" r="0.6">
          <stop offset="0" stopColor="#FFE3A6" stopOpacity="0.95" />
          <stop offset="1" stopColor="#FFAA1F" stopOpacity="0.55" />
        </radialGradient>
      </defs>
      <rect width="300" height="120" fill={`url(#sf${id})`} />
      {/* awning */}
      {Array.from({ length: 10 }, (_, i) => (
        <path key={i} d={`M${i * 30} 22 H${i * 30 + 30} V40 Q${i * 30 + 15} 50 ${i * 30} 40 Z`} fill={i % 2 ? stripe : '#F7F4EE'} opacity={i % 2 ? 1 : 0.92} />
      ))}
      <rect x="0" y="0" width="300" height="22" fill="#070B16" opacity="0.45" />
      {/* lit shop window with shelves */}
      <rect x="30" y="56" width="150" height="64" rx="6" fill={`url(#sg${id})`} />
      {[70, 92].map((y) => (
        <rect key={y} x="36" y={y} width="138" height="3" fill="#A35F00" opacity="0.55" />
      ))}
      {Array.from({ length: 12 }, (_, i) => {
        const x = 42 + (i % 6) * 22;
        const y = i < 6 ? 58 : 80;
        const tall = (h + i) % 3;
        return <rect key={i} x={x} y={y + (tall ? 0 : 4)} width="8" height={tall ? 12 : 8} rx="2" fill={['#0E1324', '#1D2D70', '#7C2D12', '#14532D'][(h + i) % 4]} opacity="0.75" />;
      })}
      {/* door */}
      <rect x="200" y="58" width="44" height="62" rx="5" fill="#0A0F1F" stroke={stripe} strokeWidth="2" />
      <circle cx="236" cy="92" r="2.4" fill={stripe} />
      <rect x="256" y="62" width="34" height="24" rx="4" fill="#F7F4EE" opacity="0.1" />
    </svg>
  );
}

